import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Show, SignIn, UserButton, useUser, useAuth } from '@clerk/react'
import { Link } from 'react-router-dom'
import { saveBlob } from '../lib/save-file'
import {
  fetchLeads,
  updateLeadStatus,
  deleteLead,
  exportLead,
  fetchBillingStatus,
  startCheckout,
  openBillingPortal,
  LeadsError,
  BillingError,
  type QuestionStatus,
  type Lead,
  type LeadStatus,
  type BillingStatus,
  fetchVoiceDrafts,
  approveVoiceDraft,
  VOLATILITY_CHOICES,
  type VoiceDraft,
  type VolatilityClass,
  fetchCounters,
  summariseCounters,
  formatWindowLabel,
  CountersError,
  type MonthlyCounters,
  type CounterTile,
  certDateToEpoch,
  fetchCaseFiles,
  fetchCaseFile,
  closeCaseFile,
  CaseFilesError,
} from '@/lib/api'
import WeeklyReportCard from '@/components/WeeklyReportCard'
import NeedsConfirmingCard from '@/components/NeedsConfirmingCard'
import { DocumentsSection } from '@/components/DocumentIntake'
import { LiveCorpusSection } from '@/components/LiveCorpus'
import {
  TabButton,
  PendingPanel,
  AnsweredPanel,
  PanelMessage,
  formatTime,
  type LoadState,
} from '@/components/QuestionQueue'
import {
  OUTCOME_LABELS,
  SOURCE_LABELS,
  CASE_FILE_OUTCOMES,
  type CaseFileDetail,
  type CaseFileOutcome,
  type CaseFileStatus,
  type CaseFileSummary,
  type CaseFileTouch,
} from '@/lib/case-file-shape'

/**
 * Supplier dashboard (Phase 5 — the human half of the self-filling loop).
 *
 * This route is LOGIN-REQUIRED. The public chat at "/" is unaffected and stays
 * open with no sign-in.
 *  - Signed-out visitors see Clerk's sign-in form. Sign-up is disabled at the
 *    Clerk instance (restricted mode), so access is invite-only.
 *  - Signed-in suppliers see their question queue: two tabs, Pending and
 *    Answered. Pending questions can be answered or dismissed; Answered is the
 *    read-only record (edit-after-approve is deliberately deferred).
 *
 * The tenant is resolved server-side from the verified session — the client
 * never sends a tenant/pod for admin calls, so a supplier only ever sees and
 * touches their own rows.
 *
 * Phase 6 (billing): a billing strip at the top of the dashboard shows the
 * supplier's subscription state and a Subscribe button (which sends them to
 * Stripe Checkout) when they are not yet entitled. The strip reads the
 * entitlement via fetchBillingStatus; the gate's source of truth is the same
 * `entitled` flag the engine computes.
 */
export default function Dashboard() {
  const { isLoaded } = useUser()

  // TAB TITLE — THE DASHBOARD IS THE PRODUCT'S SURFACE, NOT THE POD'S.
  // main.tsx titles the document from the active pod at boot, which is right
  // for the public chat but wrong here: /dashboard is the brand-neutral
  // supplier surface (its own header already says "Foredesk · Supplier
  // dashboard"), and on the chamberflow-app deployment the inherited title
  // read "Oxydise — onboarding assistant" — another business's name on a
  // paying supplier's tab. Spotted live 14 Aug 2026. The pod title is restored
  // on unmount so navigating back to the chat re-brands the tab correctly.
  //
  // NAMED "FOREDESK", NOT "CHAMBERFLOW" (11 Sep 2026). ChamberFlow is the
  // ENGINE — the internal name for the machinery, the repo and the Worker.
  // Foredesk is the PRODUCT, and it is the only one of the two a buyer has
  // ever been sold, seen on a chat surface ("Built by Foredesk") or signed a
  // contract for. This surface is the one place the engine name leaked to a
  // paying supplier: a buyer logging in to answer his own gap questions was
  // reading a product name that appears nowhere else in what he bought, and
  // being invited to subscribe to it. Same class of error as the pod title
  // leak above, one level up.
  //
  // NOT RENAMED ELSEWHERE, deliberately: the repo, the Worker, wrangler.toml
  // and the chamberflow POD all keep the name, because there the engine IS the
  // subject. The pod sells ChamberFlow to ChamberFlow's own prospects.
  useEffect(() => {
    const previous = document.title
    document.title = 'Foredesk — Supplier dashboard'
    return () => {
      document.title = previous
    }
  }, [])

  // Wait for Clerk to resolve auth state before choosing what to show, so a
  // signed-in supplier never sees a flash of the sign-in form on refresh.
  if (!isLoaded) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-base">
        <p className="text-sm text-text-muted">Loading…</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface-base">
      <Show when="signed-out">
        <div className="flex min-h-screen items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="mb-6 text-center">
              <p className="font-display text-xl font-semibold tracking-tight">Foredesk</p>
              <p className="mt-1 text-sm text-text-muted">Supplier dashboard — please sign in.</p>
            </div>
            <div className="flex justify-center">
              <SignIn forceRedirectUrl="/dashboard" />
            </div>
            <p className="mt-6 text-center text-xs text-text-muted">
              Access is invite-only.{' '}
              <Link to="/" className="text-brand-primary underline underline-offset-2">
                Back to the assistant
              </Link>
            </p>
          </div>
        </div>
      </Show>

      <Show when="signed-in">
        <DashboardHome />
      </Show>
    </div>
  )
}

function DashboardHome() {
  const { user } = useUser()
  const email = user?.primaryEmailAddress?.emailAddress ?? 'your account'
  const [view, setView] = useState<
    'leads' | 'cases' | 'questions' | 'voice' | 'documents' | 'knows'
  >('leads')

  return (
    <div className="min-h-screen">
      <header className="border-b border-border-subtle bg-surface-raised">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-3">
          <div>
            <p className="font-display font-semibold tracking-tight">Foredesk</p>
            <p className="text-xs text-text-muted">Supplier dashboard</p>
          </div>
          <UserButton />
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-8">
        <BillingStrip />
        <CountersCard />
        <WeeklyReportCard />
        <NeedsConfirmingCard />

        {/* Primary surface switch. Leads (the sales pipeline) leads; Questions
            (the self-filling corpus loop) is the second surface. */}
        <div className="flex gap-1 border-b border-border-subtle">
          <TabButton active={view === 'leads'} onClick={() => setView('leads')}>
            Leads
          </TabButton>
          <TabButton active={view === 'cases'} onClick={() => setView('cases')}>
            Case files
          </TabButton>
          <TabButton active={view === 'voice'} onClick={() => setView('voice')}>
            Voice drafts
          </TabButton>
          <TabButton active={view === 'questions'} onClick={() => setView('questions')}>
            Questions
          </TabButton>
          <TabButton active={view === 'documents'} onClick={() => setView('documents')}>
            Documents
          </TabButton>
          <TabButton active={view === 'knows'} onClick={() => setView('knows')}>
            What it knows
          </TabButton>
        </div>

        {view === 'leads' ? (
          <LeadsSection />
        ) : view === 'cases' ? (
          <CaseFilesSection />
        ) : view === 'voice' ? (
          <VoiceDraftsSection />
        ) : view === 'documents' ? (
          <DocumentsSection />
        ) : view === 'knows' ? (
          <LiveCorpusSection />
        ) : (
          <QuestionsSection email={email} />
        )}
      </main>
    </div>
  )
}

function QuestionsSection({ email }: { email: string }) {
  const [tab, setTab] = useState<QuestionStatus>('pending')

  return (
    <section className="mt-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight">Customer questions</h1>
      <p className="mt-1 text-sm text-text-muted">
        Signed in as <span className="font-medium text-text-primary">{email}</span>. Questions your
        assistant couldn't answer appear here for you to answer.
      </p>

      <div className="mt-6 flex gap-1 border-b border-border-subtle">
        <TabButton active={tab === 'pending'} onClick={() => setTab('pending')}>
          Pending
        </TabButton>
        <TabButton active={tab === 'answered'} onClick={() => setTab('answered')}>
          Answered
        </TabButton>
      </div>

      {/* Keyed so switching tabs remounts the panel and fetches fresh. */}
      {tab === 'pending' ? (
        <PendingPanel
          key="pending"
          voice
          emptyMessage="No pending questions. New ones will appear here as customers ask."
        />
      ) : (
        <AnsweredPanel
          key="answered"
          emptyMessage="No answered questions yet. Answers you save will appear here."
        />
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Leads surface — the sales pipeline half of the dashboard. Consumes the Phase 4
// lead-admin endpoints (GET /api/admin/leads, POST /api/admin/leads/:id/status),
// tenant-scoped server-side from the verified session.
// ---------------------------------------------------------------------------

const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  quoted: 'Quoted',
  won: 'Won',
  lost: 'Lost',
  dead: 'Dead',
}
const LEAD_STATUS_ORDER: LeadStatus[] = ['new', 'contacted', 'quoted', 'won', 'lost', 'dead']

function LeadsSection() {
  // Leads accumulate permanently (unlike questions, which clear as they're
  // actioned), so a filter earns its place: a supplier with a long history needs
  // to find won/lost leads without scrolling. 'all' omits the status param.
  const [filter, setFilter] = useState<LeadStatus | 'all'>('all')

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Leads</h1>
          <p className="mt-1 text-sm text-text-muted">
            Qualified enquiries your assistant captured. Ring them back and move each one through
            your pipeline.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="lead-filter" className="text-sm text-text-muted">
            Show
          </label>
          <select
            id="lead-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as LeadStatus | 'all')}
            className="rounded-lg border border-border-subtle bg-surface-raised px-3 py-1.5 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            <option value="all">All</option>
            {LEAD_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {LEAD_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Keyed by filter so changing it remounts the panel and fetches fresh. */}
      <LeadsPanel key={filter} filter={filter} />
    </section>
  )
}

function LeadsPanel({ filter }: { filter: LeadStatus | 'all' }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [leads, setLeads] = useState<Lead[]>([])
  const [state, setState] = useState<LoadState>('loading')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setState('loading')
    setError('')
    try {
      const token = await getTokenRef.current()
      const list = await fetchLeads(token, filter === 'all' ? undefined : filter)
      setLeads(list)
      setState('loaded')
    } catch (e) {
      setError(e instanceof LeadsError ? e.message : 'Could not load leads.')
      setState('error')
    }
  }, [filter])

  useEffect(() => {
    void load()
  }, [load])

  // On a status change, reload the authoritative list rather than guessing the
  // new UI state (matches the questions cards). A lead that no longer matches the
  // active filter drops out on reload.
  async function handleStatus(id: string, status: LeadStatus) {
    const token = await getTokenRef.current()
    await updateLeadStatus(token, id, status)
    await load()
  }

  // Erasure is permanent, so the card gates it behind an inline confirm; here we
  // just do the delete and reload the authoritative list (the card unmounts).
  async function handleDelete(id: string) {
    const token = await getTokenRef.current()
    await deleteLead(token, id)
    await load()
  }

  // Build 13f: a subject access request, answered from the card. The file is
  // named by the enquiry's id, never the person's name.
  async function handleExport(id: string) {
    const token = await getTokenRef.current()
    saveBlob(await exportLead(token, id), `enquiry-${id.slice(0, 8)}.json`)
  }

  if (state === 'loading') return <PanelMessage>Loading leads…</PanelMessage>
  if (state === 'error') {
    return (
      <PanelMessage tone="danger">
        {error}{' '}
        <button onClick={() => void load()} className="underline underline-offset-2">
          Try again
        </button>
      </PanelMessage>
    )
  }
  if (leads.length === 0) {
    return (
      <PanelMessage>
        No leads
        {filter === 'all' ? '' : ` marked ${LEAD_STATUS_LABELS[filter].toLowerCase()}`} yet.
        Qualified enquiries from your assistant will appear here.
      </PanelMessage>
    )
  }

  return (
    <ul className="mt-5 stack-y-4">
      {leads.map((lead) => (
        <li key={lead.id}>
          <LeadCard lead={lead} onStatus={handleStatus} onDelete={handleDelete} onExport={handleExport} />
        </li>
      ))}
    </ul>
  )
}

function LeadCard({
  lead,
  onStatus,
  onDelete,
  onExport,
}: {
  lead: Lead
  onStatus: (id: string, status: LeadStatus) => Promise<void>
  onDelete: (id: string) => Promise<void>
  onExport: (id: string) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const [exporting, setExporting] = useState(false)

  async function download() {
    setExporting(true)
    setErr('')
    try {
      await onExport(lead.id)
    } catch (e) {
      setErr(e instanceof LeadsError ? e.message : 'Could not export this enquiry.')
    } finally {
      setExporting(false)
    }
  }
  const [err, setErr] = useState('')
  // Erasure is destructive, so it is gated behind an inline two-step confirm
  // rather than firing on the first click.
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  async function changeStatus(next: LeadStatus) {
    if (next === lead.status) return
    setBusy(true)
    setErr('')
    try {
      await onStatus(lead.id, next)
    } catch (e) {
      setErr(e instanceof LeadsError ? e.message : 'Could not update this lead.')
    } finally {
      // WAS: reset on failure only, on the reasoning that "the list reloads and
      // this card may unmount, so no reset is needed". MAY unmount is the
      // problem — when it does not, the card is left disabled over a write that
      // succeeded, and the owner reads that as a failure. The publish button
      // showed exactly this in production, from exactly this reasoning. A
      // finally costs nothing and does not depend on what a parent does next.
      setBusy(false)
    }
  }

  async function confirmDelete() {
    setDeleting(true)
    setErr('')
    try {
      await onDelete(lead.id)
      // On success the list reloads and this card unmounts — no reset needed.
    } catch (e) {
      setErr(e instanceof LeadsError ? e.message : 'Could not delete this lead.')
      setDeleting(false)
      setConfirmingDelete(false)
    }
  }

  return (
    <div className="rounded-card border border-border-subtle bg-surface-raised p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium text-text-primary">{lead.customer_name}</p>
            <ScoreBadge score={lead.lead_score} />
          </div>
          <a
            href={`tel:${lead.customer_phone}`}
            className="text-sm text-brand-primary underline-offset-2 hover:underline"
          >
            {lead.customer_phone}
          </a>
        </div>
        <p className="shrink-0 text-xs text-text-muted">{formatTime(lead.created_at)}</p>
      </div>

      {lead.suggested_opener ? (
        <p className="mt-3 rounded-lg bg-surface-base px-3 py-2 text-sm text-text-primary">
          {lead.suggested_opener}
        </p>
      ) : null}

      <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <MetaItem label="Enquiry" value={lead.job_type} />
        <MetaItem label="Details" value={lead.property_type} />
        <MetaItem label="Indicative price" value={lead.estimated_value} />
        <MetaItem label="Area" value={lead.area} />
        <MetaItem label="Preferred callback" value={lead.callback_time} />
      </dl>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <label htmlFor={`status-${lead.id}`} className="text-sm text-text-muted">
            Status
          </label>
          <select
            id={`status-${lead.id}`}
            value={lead.status}
            disabled={busy || deleting}
            onChange={(e) => void changeStatus(e.target.value as LeadStatus)}
            className="rounded-lg border border-border-subtle bg-surface-base px-3 py-1.5 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {LEAD_STATUS_ORDER.map((s) => (
              <option key={s} value={s}>
                {LEAD_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          {busy ? <span className="text-xs text-text-muted">Saving…</span> : null}
        </div>

        {confirmingDelete ? (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-text-muted">Delete — can't be undone?</span>
            <button
              onClick={() => void confirmDelete()}
              disabled={deleting}
              className="rounded-lg border border-border-subtle px-3 py-1.5 font-medium text-text-danger transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              {deleting ? 'Deleting…' : 'Confirm'}
            </button>
            <button
              onClick={() => setConfirmingDelete(false)}
              disabled={deleting}
              className="rounded-lg px-3 py-1.5 text-text-muted transition-colors hover:text-text-primary disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <button
              onClick={() => void download()}
              disabled={busy || exporting}
              title="Everything held about this enquiry, as a file to send to the person if they ask for it"
              className="text-sm text-text-muted underline-offset-2 transition-colors hover:text-text-primary hover:underline disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
            >
              {exporting ? 'Preparing…' : 'Download their data'}
            </button>
            <button
              onClick={() => setConfirmingDelete(true)}
              disabled={busy}
              className="text-sm text-text-muted underline-offset-2 transition-colors hover:text-text-danger hover:underline disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {err ? <p className="mt-2 text-sm text-text-danger">{err}</p> : null}
    </div>
  )
}

function ScoreBadge({ score }: { score: 'hot' | 'warm' | 'cold' }) {
  // Only the real token palette is available (no amber/green): hot borrows the
  // danger red (urgency), warm the brand teal, cold the muted surface.
  const styles: Record<'hot' | 'warm' | 'cold', string> = {
    hot: 'bg-[rgb(var(--color-text-danger)/0.1)] text-text-danger',
    warm: 'bg-[rgb(var(--color-brand-primary)/0.1)] text-brand-primary',
    cold: 'bg-surface-subtle text-text-muted',
  }
  return (
    <span className={'rounded-full px-2 py-0.5 text-xs font-medium capitalize ' + styles[score]}>
      {score}
    </span>
  )
}

/** One label/value pair in a lead's metadata row. Renders nothing when the value
 *  is empty (the engine sends '' for fields a pod's niche doesn't populate). */
function MetaItem({ label, value }: { label: string; value: string }) {
  if (!value) return null
  return (
    <div>
      <dt className="text-xs text-text-muted">{label}</dt>
      <dd className="text-text-primary">{value}</dd>
    </div>
  )
}

/**
 * Billing strip. Reads the supplier's entitlement and renders one of:
 *  - loading: a quiet placeholder (no layout jump).
 *  - entitled: a subscribed confirmation (Manage billing arrives next step).
 *  - not entitled: a Subscribe button that opens Stripe Checkout.
 *  - error: a non-blocking note with a retry (never blocks the questions UI).
 * On return from Checkout the URL carries ?billing=success|cancelled; we read it
 * only to show a one-line confirmation, then the real state comes from the fetch.
 */
function BillingStrip() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [error, setError] = useState('')
  const [starting, setStarting] = useState(false)

  const load = useCallback(async () => {
    setState('loading')
    setError('')
    try {
      const token = await getTokenRef.current()
      const s = await fetchBillingStatus(token)
      setStatus(s)
      setState('loaded')
    } catch (e) {
      setError(e instanceof BillingError ? e.message : 'Could not load billing status.')
      setState('error')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function handleSubscribe() {
    setStarting(true)
    setError('')
    try {
      const token = await getTokenRef.current()
      const url = await startCheckout(token)
      // Send the supplier to Stripe's hosted checkout.
      window.location.href = url
    } catch (e) {
      setError(e instanceof BillingError ? e.message : 'Could not start checkout. Please try again.')
      setStarting(false)
    }
  }

  async function handleManage() {
    setStarting(true)
    setError('')
    try {
      const token = await getTokenRef.current()
      const url = await openBillingPortal(token)
      // Send the supplier to Stripe's hosted Customer Portal.
      window.location.href = url
    } catch (e) {
      setError(e instanceof BillingError ? e.message : 'Could not open billing management.')
      setStarting(false)
    }
  }

  // A returning-from-Checkout hint (purely cosmetic; the fetch is authoritative).
  const params = new URLSearchParams(window.location.search)
  const justReturned = params.get('billing')

  if (state === 'loading') {
    return (
      <div className="mb-6 h-12 animate-pulse rounded-card border border-border-subtle bg-surface-raised" />
    )
  }

  if (state === 'error') {
    return (
      <div className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-3 text-sm text-text-danger">
        {error}{' '}
        <button onClick={() => void load()} className="underline underline-offset-2">
          Try again
        </button>
      </div>
    )
  }

  // Loaded.
  // The operator's own pod: nothing to pay, nothing to manage. Stated plainly
  // instead of either nagging the operator to subscribe to himself (what this
  // strip did while entitlement rode the fail-open branch) or showing
  // "Subscription active" over a subscription that does not exist.
  if (status?.entitled && status.reason === 'operator') {
    return (
      <div className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-3">
        <p className="text-sm font-medium text-text-primary">Your own product</p>
        <p className="text-xs text-text-muted">
          This is an operator account — no subscription is needed.
        </p>
        {error ? <p className="mt-1 text-sm text-text-danger">{error}</p> : null}
      </div>
    )
  }
  if (status?.entitled) {
    return (
      <div className="mb-6 flex items-center justify-between rounded-card border border-border-subtle bg-surface-raised px-4 py-3">
        <div>
          <p className="text-sm font-medium text-text-primary">Subscription active</p>
          {status.currentPeriodEnd ? (
            <p className="text-xs text-text-muted">
              Renews {formatTime(status.currentPeriodEnd)}
            </p>
          ) : null}
          {error ? <p className="mt-1 text-sm text-text-danger">{error}</p> : null}
        </div>
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-surface-base px-3 py-1 text-xs font-medium text-text-muted">
            Foredesk · £99/mo
          </span>
          <button
            onClick={handleManage}
            disabled={starting}
            className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-primary transition-colors hover:bg-surface-base disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            {starting ? 'Opening…' : 'Manage billing'}
          </button>
        </div>
      </div>
    )
  }

  // Not entitled — show Subscribe.
  return (
    <div className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-text-primary">
            {status?.status ? 'Subscription inactive' : 'Subscribe to Foredesk'}
          </p>
          <p className="text-xs text-text-muted">
            {status?.status
              ? 'Your subscription is not active. Resubscribe to keep your assistant running.'
              : 'Activate your assistant with a Foredesk subscription.'}
          </p>
        </div>
        <button
          onClick={handleSubscribe}
          disabled={starting}
          className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
        >
          {starting ? 'Starting…' : 'Subscribe — £99/mo'}
        </button>
      </div>
      {justReturned === 'cancelled' ? (
        <p className="mt-2 text-xs text-text-muted">Checkout was cancelled — no charge was made.</p>
      ) : null}
      {error ? <p className="mt-2 text-sm text-text-danger">{error}</p> : null}
    </div>
  )
}

/**
 * The four counters — the owner's monthly report, above the queues he works.
 *
 * THIS COMPONENT DOES NO ARITHMETIC AND CHOOSES NO WORDS. Every number, label
 * and caveat comes from summariseCounters() in the engine's dependency-free
 * counters-format module, which has a suite. The rules it enforces all guard
 * against the same failure — two different truths rendering as the same digit:
 * "nobody asked" must not read as 0%, and a counter whose emitter has not
 * shipped must not read as a measured zero. Putting those rules in JSX would
 * put them somewhere nothing can assert them.
 *
 * NOT-LIVE TILES ARE MUTED, NOT FADED. opacity-50 over body text drops contrast
 * below WCAG AA, so a tile that is not switched on renders its dash in the muted
 * text colour and SAYS it is not switched on. The state is carried by words,
 * never by dimness alone.
 *
 * A failure here is non-blocking by construction: the card collapses to one
 * quiet line with a retry, and the leads/questions surfaces below it carry on.
 * The report is the least urgent thing on this screen.
 */
function CountersCard() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [counters, setCounters] = useState<MonthlyCounters | null>(null)
  const [state, setState] = useState<'loading' | 'loaded' | 'error'>('loading')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setState('loading')
    setError('')
    try {
      const token = await getTokenRef.current()
      setCounters(await fetchCounters(token))
      setState('loaded')
    } catch (e) {
      setError(e instanceof CountersError ? e.message : 'Could not load your numbers.')
      setState('error')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const tiles = useMemo(() => (counters ? summariseCounters(counters) : []), [counters])

  if (state === 'loading') {
    return (
      <div className="mb-6 h-28 animate-pulse rounded-card border border-border-subtle bg-surface-raised" />
    )
  }

  if (state === 'error' || !counters) {
    return (
      <div className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-3 text-sm text-text-danger">
        {error || 'Could not load your numbers.'}{' '}
        <button onClick={() => void load()} className="underline underline-offset-2">
          Try again
        </button>
      </div>
    )
  }

  const month = formatWindowLabel(counters.fromSeconds)

  return (
    <section className="mb-6 rounded-card border border-border-subtle bg-surface-raised px-4 py-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-sm font-semibold tracking-tight text-text-primary">
          How your assistant is doing
        </h2>
        <p className="text-xs text-text-muted">{month ? `${month} so far` : 'This month so far'}</p>
      </div>

      <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <CounterTileView key={t.key} tile={t} />
        ))}
      </dl>
    </section>
  )
}

function CounterTileView({ tile }: { tile: CounterTile }) {
  return (
    <div>
      <dt className="text-xs font-medium text-text-muted">{tile.label}</dt>
      <dd
        className={
          'mt-1 font-display text-2xl font-semibold tracking-tight ' +
          (tile.live ? 'text-text-primary' : 'text-text-muted')
        }
      >
        {tile.value}
      </dd>
      <p className="mt-1 text-xs text-text-muted">{tile.detail}</p>
      {tile.note ? <p className="mt-0.5 text-xs text-text-muted">{tile.note}</p> : null}
    </div>
  )
}

/* ---------- case files (Build 8) ---------- */

/**
 * CASE FILES — one customer's whole history, in one place.
 *
 * Every other surface on this dashboard is organised by THING: leads, questions,
 * documents, facts. This one is organised by PERSON. It is the only screen that
 * answers "what has this customer and I actually done?" across the chat, the
 * missed-call bridge, WhatsApp and the chases we sent — and that question is the
 * one an owner is holding in their head when they pick the phone up.
 *
 * PER ENQUIRY, NOT PER CUSTOMER. A file covers one piece of work and ends with
 * one outcome. The same person opens a new file next time. That is why closing
 * matters and why the copy says "this enquiry" rather than "this customer".
 *
 * CLOSING IS PERMANENT. There is no reopening — the engine enforces it, not this
 * screen — so the outcome buttons are gated behind a two-step confirm, the same
 * shape lead erasure already uses.
 */
function CaseFilesSection() {
  // Open leads, because the open ones are the work. 'all' is there for looking
  // back at what an enquiry came to, which is a different job done less often.
  const [filter, setFilter] = useState<CaseFileStatus | 'all'>('open')

  return (
    <section className="mt-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Case files</h1>
          <p className="mt-1 text-sm text-text-muted">
            One enquiry, every channel it arrived through, in the order it happened. Close a file
            when the work is settled so the next enquiry from the same person starts its own.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="case-filter" className="text-sm text-text-muted">
            Show
          </label>
          <select
            id="case-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value as CaseFileStatus | 'all')}
            className="rounded-lg border border-border-subtle bg-surface-raised px-3 py-1.5 text-sm text-text-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="all">All</option>
          </select>
        </div>
      </div>

      {/* Keyed by filter so changing it remounts the panel and fetches fresh. */}
      <CaseFilesPanel key={filter} filter={filter} />
    </section>
  )
}

function CaseFilesPanel({ filter }: { filter: CaseFileStatus | 'all' }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [files, setFiles] = useState<CaseFileSummary[]>([])
  const [truncated, setTruncated] = useState(false)
  const [state, setState] = useState<LoadState>('loading')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setState('loading')
    setError('')
    try {
      const token = await getTokenRef.current()
      const result = await fetchCaseFiles(token, filter === 'all' ? undefined : filter)
      setFiles(result.caseFiles)
      setTruncated(result.truncated)
      setState('loaded')
    } catch (e) {
      setError(e instanceof CaseFilesError ? e.message : 'Could not load case files.')
      setState('error')
    }
  }, [filter])

  useEffect(() => {
    void load()
  }, [load])

  if (state === 'loading') return <PanelMessage>Loading case files…</PanelMessage>
  if (state === 'error') {
    return (
      <PanelMessage tone="danger">
        {error}{' '}
        <button onClick={() => void load()} className="underline underline-offset-2">
          Try again
        </button>
      </PanelMessage>
    )
  }
  if (files.length === 0) {
    return (
      <PanelMessage>
        {filter === 'closed'
          ? 'No closed case files yet.'
          : 'No case files yet. One opens the first time somebody contacts you — by chat, missed call or WhatsApp — and every later contact from the same person joins it.'}
      </PanelMessage>
    )
  }

  return (
    <>
      {/* The cap is REPORTED, never silent. Telling an owner they have seen
          everything when they have not is the one thing a list must not do. */}
      {truncated && (
        <PanelMessage>
          Showing the {files.length} most recent. Close some files to see further back.
        </PanelMessage>
      )}
      <ul className="mt-5 stack-y-4">
        {files.map((file) => (
          <li key={file.id}>
            <CaseFileCard summary={file} onClosed={load} />
          </li>
        ))}
      </ul>
    </>
  )
}

/**
 * One case file: the summary always, the timeline on demand.
 *
 * The timeline is a SECOND request, fetched when the owner opens the card. The
 * list endpoint deliberately does not carry touches — a supplier with two
 * hundred files would otherwise pull every touch of every one of them to render
 * a screen that shows a name and a count.
 */
function CaseFileCard({
  summary,
  onClosed,
}: {
  summary: CaseFileSummary
  onClosed: () => Promise<void>
}) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState<CaseFileDetail | null>(null)
  const [detailState, setDetailState] = useState<LoadState>('loading')
  const [detailError, setDetailError] = useState('')

  const [confirming, setConfirming] = useState(false)
  const [closing, setClosing] = useState(false)
  const [closeError, setCloseError] = useState('')

  const loadDetail = useCallback(async () => {
    setDetailState('loading')
    setDetailError('')
    try {
      const token = await getTokenRef.current()
      setDetail(await fetchCaseFile(token, summary.id))
      setDetailState('loaded')
    } catch (e) {
      setDetailError(e instanceof CaseFilesError ? e.message : 'Could not load this case file.')
      setDetailState('error')
    }
  }, [summary.id])

  function toggle() {
    const next = !open
    setOpen(next)
    if (next && detail === null) void loadDetail()
  }

  async function close(outcome: CaseFileOutcome) {
    setClosing(true)
    setCloseError('')
    try {
      const token = await getTokenRef.current()
      await closeCaseFile(token, summary.id, outcome)
      await onClosed()
    } catch (e) {
      // A 409 is NOT a failure — it is a stale tab, and the engine tells us what
      // the file was already closed as. Saying so is the difference between a
      // shrug and a support call about data the owner thinks has been lost.
      if (e instanceof CaseFilesError && e.alreadyClosedOutcome) {
        setCloseError(
          `Already closed as ${OUTCOME_LABELS[e.alreadyClosedOutcome].toLowerCase()} — ` +
            'someone else may have got there first. Refresh to see it.',
        )
      } else {
        setCloseError(e instanceof CaseFilesError ? e.message : 'Could not close this case file.')
      }
      setConfirming(false)
    } finally {
      // Reset even on success. The list reloads and this card MAY unmount — and
      // when it does not, a card left disabled over a write that succeeded reads
      // to the owner as a failure. That exact reasoning shipped the stuck
      // publish button, so it is not repeated here.
      setClosing(false)
    }
  }

  const name = summary.contact?.name
  const phone = summary.contact?.phone

  return (
    <article className="rounded-card border border-border-subtle bg-surface-raised">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-display font-semibold tracking-tight">
              {/* No lead means nobody has given us their details on any channel.
                  Saying so plainly beats a placeholder that reads like a bug. */}
              {name ?? phone ?? 'Contact details not held'}
            </h2>
            {summary.status === 'closed' && summary.outcome && (
              <span className="rounded-full border border-border-subtle px-2 py-0.5 text-xs text-text-muted">
                {OUTCOME_LABELS[summary.outcome]}
              </span>
            )}
          </div>
          {name && phone && (
            <a
              href={`tel:${phone.replace(/\s/g, '')}`}
              className="text-sm text-brand-primary underline underline-offset-2 hover:no-underline focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent rounded"
            >
              {phone}
            </a>
          )}
          <p className="mt-1 text-xs text-text-muted">
            {summary.touchCount} {summary.touchCount === 1 ? 'contact' : 'contacts'}
            {summary.channels.length > 0 &&
              ` · ${summary.channels.map((c) => SOURCE_LABELS[c]).join(', ')}`}
            {summary.lastTouchAt !== null && ` · last ${formatTime(summary.lastTouchAt)}`}
          </p>
        </div>

        <button
          onClick={toggle}
          aria-expanded={open}
          className="shrink-0 rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent hover:text-brand-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
        >
          {open ? 'Hide history' : 'View history'}
        </button>
      </div>

      {open && (
        <div className="border-t border-border-subtle px-4 py-3">
          {detailState === 'loading' && <p className="text-sm text-text-muted">Loading history…</p>}
          {detailState === 'error' && (
            <p className="text-sm text-text-danger">
              {detailError}{' '}
              <button onClick={() => void loadDetail()} className="underline underline-offset-2">
                Try again
              </button>
            </p>
          )}
          {detailState === 'loaded' && detail && <CaseFileTimeline detail={detail} />}

          {summary.status === 'open' && (
            <div className="mt-4 border-t border-border-subtle pt-3">
              {closeError && <p className="mb-2 text-sm text-text-danger">{closeError}</p>}
              {confirming ? (
                <div>
                  <p className="text-sm text-text-primary">
                    How did this enquiry end? This is permanent — a closed file cannot be reopened.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {CASE_FILE_OUTCOMES.map((outcome) => (
                      <button
                        key={outcome}
                        onClick={() => void close(outcome)}
                        disabled={closing}
                        className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent hover:text-brand-primary disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
                      >
                        {OUTCOME_LABELS[outcome]}
                      </button>
                    ))}
                    <button
                      onClick={() => setConfirming(false)}
                      disabled={closing}
                      className="rounded-lg px-3 py-1.5 text-sm text-text-muted underline underline-offset-2 disabled:opacity-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setConfirming(true)}
                  className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:border-brand-accent hover:text-brand-primary focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-accent"
                >
                  Close this file
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </article>
  )
}

/** The timeline: every contact, oldest first, with what it was. */
function CaseFileTimeline({ detail }: { detail: CaseFileDetail }) {
  if (detail.touches.length === 0) {
    return <p className="text-sm text-text-muted">No contacts recorded on this file.</p>
  }
  return (
    <>
      <ol className="stack-y-3">
        {detail.touches.map((touch) => (
          <li key={touch.id} className="border-l-2 border-border-subtle pl-3">
            <p className="text-sm font-medium text-text-primary">
              {SOURCE_LABELS[touch.source]}
              <span className="ml-2 font-normal text-xs text-text-muted">
                {formatTime(touch.occurredAt)}
              </span>
            </p>
            <TouchDetailLine touch={touch} />
          </li>
        ))}
      </ol>
      {detail.truncated && (
        <p className="mt-3 text-xs text-text-muted">
          Showing the {detail.touches.length} earliest contacts of {detail.touchCount}.
        </p>
      )}
    </>
  )
}

/**
 * What one contact was.
 *
 * A null detail means the source row has been ERASED — under a right-to-erasure
 * request, or by its retention period expiring. The contact still happened, so
 * it stays on the timeline and says what became of it. Hiding the row would make
 * the history claim an enquiry never arrived; showing a blank would look like a
 * bug. Neither is the truth, and the truth is short enough to just say.
 */
function TouchDetailLine({ touch }: { touch: CaseFileTouch }) {
  if (touch.detail === null) {
    return <p className="text-sm text-text-muted">Details erased.</p>
  }
  switch (touch.detail.kind) {
    case 'lead': {
      const d = touch.detail
      const bits = [d.jobType, d.propertyType, d.area].filter((s) => s !== '')
      return (
        <p className="text-sm text-text-muted">
          {bits.length > 0 ? bits.join(' · ') : 'Enquiry captured.'}
          {d.estimatedValue !== '' && ` · ${d.estimatedValue}`}
        </p>
      )
    }
    case 'missed_call':
      return (
        <p className="text-sm text-text-muted">
          {touch.detail.outcome === 'bridged'
            ? 'We texted them back for you.'
            : touch.detail.outcome === 'opted_out'
              ? 'Not texted back: they had asked you to stop texting them.'
              : `Call recorded — ${touch.detail.outcome.replace(/_/g, ' ')}.`}
        </p>
      )
    case 'whatsapp_message':
      return (
        <p className="text-sm text-text-muted">
          {touch.detail.outcome === 'replied'
            ? 'Answered on WhatsApp.'
            : `WhatsApp message — ${touch.detail.outcome.replace(/_/g, ' ')}.`}
        </p>
      )
    case 'follow_up':
      return (
        <p className="text-sm text-text-muted">
          Chase sent{touch.detail.sentAt !== null && ` at ${formatTime(touch.detail.sentAt)}`}.
        </p>
      )
  }
}

/* ---------- voice drafts ---------- */

/**
 * The approve screen for spoken answers (Build 1).
 *
 * This is where a voice note becomes a fact a customer can be told. Two things
 * about it are deliberate and load-bearing:
 *
 * THE PROPOSED VOLATILITY IS VISIBLE, NOT SILENT. The engine proposes how often
 * a fact should be re-confirmed, and that proposal is rendered on this screen
 * with a one-tap override. If it were applied invisibly it would be a hidden
 * default wearing a friendlier name, and its failure mode would be silent: a
 * price quietly classed as long-lived answers stale for a year and nothing ever
 * flags it. Shown, the owner corrects it in a second.
 *
 * THE TRANSCRIPT IS EDITABLE BEFORE APPROVAL. What he approves is what becomes
 * the fact; the original transcript stays on the capture as evidence of what was
 * actually said. Speech recognition is good, not perfect, and the whole promise
 * of the product is that nothing reaches a customer without an owner standing
 * behind it.
 */
function VoiceDraftsSection() {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [drafts, setDrafts] = useState<VoiceDraft[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const token = await getTokenRef.current()
      setDrafts(await fetchVoiceDrafts(token))
    } catch {
      setError('Could not load your voice drafts.')
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (error) {
    return (
      <div className="rounded-card border border-border-subtle bg-surface-raised px-4 py-3">
        <p className="text-sm text-text-danger">{error}</p>
      </div>
    )
  }
  if (drafts === null) return <p className="text-text-muted">Loading…</p>
  if (drafts.length === 0) {
    return (
      <div>
        <h2 className="font-display text-xl font-semibold tracking-tight">Voice drafts</h2>
        <p className="mt-1 text-text-muted">
          Answers you have recorded appear here for you to check and approve. Nothing is published
          until you do.
        </p>
        <p className="mt-6 text-text-muted">No drafts waiting.</p>
      </div>
    )
  }

  return (
    <div>
      <h2 className="font-display text-xl font-semibold tracking-tight">Voice drafts</h2>
      <p className="mt-1 text-text-muted">
        Check the wording, then approve. Once approved, your assistant can use it to answer
        customers.
      </p>
      <ul className="mt-6 stack-y-4">
        {drafts.map((d) => (
          <li key={d.id}>
            <DraftCard draft={d} onDone={load} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function DraftCard({ draft, onDone }: { draft: VoiceDraft; onDone: () => void }) {
  const { getToken } = useAuth()
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken

  const [content, setContent] = useState(draft.transcript)
  // Seeded from the engine's proposal — which the owner can SEE, right below.
  const [volatility, setVolatility] = useState<VolatilityClass>(draft.proposedVolatility)
  const [certDate, setCertDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const needsCertDate = volatility === 'cert_tied'
  const canApprove = content.trim().length > 0 && (!needsCertDate || certDate !== '') && !busy

  async function approve() {
    setBusy(true)
    setError(null)
    try {
      const token = await getTokenRef.current()
      await approveVoiceDraft(token, draft.id, {
        content: content.trim(),
        volatilityClass: volatility,
        // The SHARED conversion (workers/decay-format.ts), not a local one. A
        // date input's value parses as UTC MIDNIGHT, so the previous
        // `new Date(certDate).getTime()` recorded a certificate as expiring at
        // the START of its last valid day — the assistant would begin hedging
        // live cover twenty-four hours early. One conversion, one test, every
        // caller.
        certExpiresAt: needsCertDate ? certDateToEpoch(certDate) ?? undefined : undefined,
      })
      onDone()
    } catch {
      setError('Could not approve that. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-card border border-border-subtle bg-surface-raised px-4 py-4">
      <label htmlFor={`draft-${draft.id}`} className="text-sm font-medium text-text-primary">
        What you said
      </label>
      <textarea
        id={`draft-${draft.id}`}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        className="mt-1.5 w-full resize-y rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-text-primary focus:border-brand-accent focus:outline-hidden"
      />
      {draft.confidence !== null && draft.confidence < 0.6 && (
        <p className="mt-1.5 text-sm text-text-muted">
          Some of this was hard to make out — worth a read before you approve it.
        </p>
      )}

      <div className="mt-4">
        <label
          htmlFor={`vol-${draft.id}`}
          className="text-sm font-medium text-text-primary"
        >
          How often should we check this is still right?
        </label>
        <p className="mt-0.5 text-sm text-text-muted">{draft.volatilityReason}</p>
        <select
          id={`vol-${draft.id}`}
          value={volatility}
          onChange={(e) => setVolatility(e.target.value as VolatilityClass)}
          className="mt-1.5 w-full rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-text-primary focus:border-brand-accent focus:outline-hidden"
        >
          {VOLATILITY_CHOICES.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </select>
      </div>

      {needsCertDate && (
        <div className="mt-3">
          <label htmlFor={`cert-${draft.id}`} className="text-sm font-medium text-text-primary">
            When does the certificate run out?
          </label>
          <input
            id={`cert-${draft.id}`}
            type="date"
            value={certDate}
            onChange={(e) => setCertDate(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-border-subtle bg-surface-base px-3 py-2 text-text-primary focus:border-brand-accent focus:outline-hidden"
          />
        </div>
      )}

      {error && <p className="mt-3 text-sm text-text-danger">{error}</p>}

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => void approve()}
          disabled={!canApprove}
          className="rounded-lg bg-brand-primary px-4 py-2 text-white transition-opacity hover:opacity-90 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-brand-accent"
        >
          {busy ? 'Approving…' : 'Approve'}
        </button>
        {needsCertDate && certDate === '' && (
          <span className="text-sm text-text-muted">Add the expiry date to approve.</span>
        )}
      </div>
    </div>
  )
}
